import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, buildImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Star, StarOff } from "lucide-react";

export default function FeaturedManager({ products, onChanged }) {
  const [saving, setSaving] = useState(false);

  const featured = useMemo(
    () =>
      [...products.filter((p) => p.featured)].sort(
        (a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999),
      ),
    [products],
  );
  const others = useMemo(
    () => products.filter((p) => !p.featured),
    [products],
  );

  const persistOrder = async (ids) => {
    setSaving(true);
    try {
      await api.put("/admin/products/featured-order", { ordered_ids: ids });
      await onChanged();
    } catch {
      toast.error("Reorder failed");
    } finally {
      setSaving(false);
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= featured.length) return;
    const ids = featured.map((p) => p.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    await persistOrder(ids);
  };

  const toggle = async (product) => {
    setSaving(true);
    try {
      await api.post(`/admin/products/${product.id}/toggle-featured`);
      await onChanged();
      toast.success(product.featured ? "Removed from Featured" : "Added to Featured");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="featured-manager">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold text-[#0B132B]">
            Featured on Homepage <span className="text-slate-400 font-normal">({featured.length})</span>
          </h3>
          <p className="text-xs text-slate-500">Order controls where products appear on the home page.</p>
        </div>
        {featured.length === 0 ? (
          <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-8 text-center">
            No featured products yet. Add products from the list below.
          </p>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y">
            {featured.map((p, idx) => (
              <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`featured-row-${p.id}`}>
                <div className="flex flex-col gap-1">
                  <Button
                    size="icon" variant="outline"
                    disabled={saving || idx === 0}
                    onClick={() => move(idx, -1)}
                    className="rounded-full w-8 h-8"
                    data-testid={`featured-up-${p.id}`}
                    aria-label="Move up"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon" variant="outline"
                    disabled={saving || idx === featured.length - 1}
                    onClick={() => move(idx, 1)}
                    className="rounded-full w-8 h-8"
                    data-testid={`featured-down-${p.id}`}
                    aria-label="Move down"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="w-8 text-center text-sm font-semibold text-slate-500">#{idx + 1}</div>
                {p.images?.[0] && (
                  <img
                    src={buildImageUrl(p.images[0])}
                    alt=""
                    className="w-14 h-14 object-contain bg-slate-50 rounded-lg shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.brand} · {p.category}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggle(p)}
                  disabled={saving}
                  className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                  data-testid={`featured-remove-${p.id}`}
                >
                  <StarOff className="w-3.5 h-3.5 me-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold text-[#0B132B] mb-3">
          Available Products <span className="text-slate-400 font-normal">({others.length})</span>
        </h3>
        <div className="bg-white border border-slate-200 rounded-2xl divide-y max-h-[520px] overflow-y-auto">
          {others.map((p) => (
            <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`available-row-${p.id}`}>
              {p.images?.[0] && (
                <img
                  src={buildImageUrl(p.images[0])}
                  alt=""
                  className="w-12 h-12 object-contain bg-slate-50 rounded-lg shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.name}</p>
                <p className="text-xs text-slate-500">{p.brand} · {p.category}</p>
              </div>
              <Switch
                checked={p.featured}
                onCheckedChange={() => toggle(p)}
                disabled={saving}
                data-testid={`feature-toggle-${p.id}`}
                aria-label="Feature"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggle(p)}
                disabled={saving}
                className="rounded-full"
                data-testid={`featured-add-${p.id}`}
              >
                <Star className="w-3.5 h-3.5 me-1" /> Add
              </Button>
            </div>
          ))}
          {others.length === 0 && (
            <p className="p-6 text-sm text-slate-500 text-center">All products are already featured.</p>
          )}
        </div>
      </div>
    </div>
  );
}
